import { FeatureCollection, GeoJsonProperties, Geometry } from 'geojson';
import { Expression } from 'mapbox-gl';
import { ErrorBoundary } from 'react-error-boundary';
import { Layer, Source } from 'react-map-gl';
import { useParams } from 'react-router-dom';

import { MAP_LAYER_FACTS } from '@/api/rest/resources/types/fact';
import { useIsPlotPage } from '@/hooks/useIsPlotPage';
import { useOrganizationIndicatorReportingFact } from '@/pages/shared/hooks/useOrganizationFact';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { getFactFromFactArray } from '@/pages/shared/hooks/utils';
import { isNullish } from '@/utils/booleans';
import { withRequestMethod } from '@/utils/mapServer';

import { LayerProps, MAP_ORIGIN, RasterProps, SIEMENS_HACK_PROTECTED_AREAS_LAYER } from './constants';
import { useControlsContext } from './hooks/useFormControls';
import { useLayerOpacity } from './hooks/useLayerOpacity';
import { useMapEndpointOrg, useMapEndpointPlot } from './hooks/useMapEndpoint';
import { MapLayerFilter } from './hooks/useMapLayerFilter';
import { useSiemensData } from './hooks/useSiemensData';

const PROTECTED_AREAS_LAYER_ID = 'pa-layer';
export const INTERACTIVE_HEATMAP_LAYER_ID = 'heatmap-layer';

export const RasterSourceLayer = ({
  layer,
  legendFilter,
  mapFilter,
}: {
  layer: LayerProps | undefined;
  legendFilter?: MapLayerFilter;
  mapFilter?: MapLayerFilter;
}) => {
  const isPlotPath = useIsPlotPage();

  return (
    <ErrorBoundary FallbackComponent={() => <></>}>
      {isPlotPath ? (
        <RasterSourceLayerPlot layer={layer} legendFilter={legendFilter?.filter} mapFilter={mapFilter?.filter} />
      ) : (
        <RasterSourceLayerOrg layer={layer} legendFilter={legendFilter?.filter} mapFilter={mapFilter?.filter} />
      )}
    </ErrorBoundary>
  );
};

const RasterSourceLayerPlot = ({
  layer,
  legendFilter,
  mapFilter,
}: {
  layer: LayerProps | undefined;
  legendFilter?: string | null;
  mapFilter?: string | null;
}) => {
  const controlsForm = useControlsContext();
  const [year] = controlsForm.watch(['year']);

  const rasterLayerUrl = useMapEndpointPlot(layer, year, legendFilter, mapFilter);

  return <RasterSourceLayerBase layer={layer} rasterLayerUrl={rasterLayerUrl} />;
};

const RasterSourceLayerOrg = ({
  layer,
  legendFilter,
  mapFilter,
}: {
  layer: LayerProps | undefined;
  legendFilter?: string | null;
  mapFilter?: string | null;
}) => {
  const controlsForm = useControlsContext();
  const [year] = controlsForm.watch(['year']);

  const { url, method } = useMapEndpointOrg(layer, year, legendFilter, mapFilter);

  const parsedUrls = getOrgRasterUrls(url);

  return (
    <>
      {parsedUrls.map((parsedUrl, index) => (
        <RasterSourceLayerBase
          key={`org-raster-layer-${index}`}
          layer={layer}
          rasterLayerUrl={parsedUrl}
          method={method}
          sourceSuffix={`${index}`}
        />
      ))}
    </>
  );
};

export const getOrgRasterUrls = (url: string): string[] => {
  const layersMatch = url.match(/([?&])layers=([^&]*)/);

  if (!layersMatch) {
    return [url];
  }

  const [fullMatch, prefix, layersParam] = layersMatch;
  const layers = (layersParam ?? '').split(',');

  if (layers.length <= 1) {
    return [url];
  }

  return layers.map((layer) => url.replace(fullMatch, `${prefix}layers=${layer}`));
};

const RasterSourceLayerBase = ({
  layer,
  rasterLayerUrl,
  method,
  sourceSuffix,
}: {
  layer: LayerProps | undefined;
  rasterLayerUrl: string;
  method?: 'POST' | 'GET';
  sourceSuffix?: string;
}) => {
  const controlsForm = useControlsContext();
  const [year] = controlsForm.watch(['year']);
  const layerName = layer?.value ?? '';

  const layerOpacity = useLayerOpacity(layerName);

  const layerOrigin = layer?.origin || MAP_ORIGIN.cckp;
  const url = withRequestMethod(rasterLayerUrl, method);
  const idBase = `${layer?.layerSubType ?? layerName}-${sourceSuffix ?? ''}`;

  return (
    <Source
      key={`${idBase}-${year}-${url}`} // Force re-render when layer or year changes
      id={`heatmap-source-${idBase}`}
      type='raster'
      tiles={[url]}
      tileSize={256}
      scheme={layerOrigin === MAP_ORIGIN.gbif ? 'xyz' : 'tms'}
    >
      <Layer
        id={`heatmap-layer-raster-${idBase}`}
        type='raster'
        paint={{
          'raster-opacity': layerOpacity,
        }}
      />
    </Source>
  );
};

export const GeojsonSourceLayer = ({
  layer,
  legendFilter,
}: {
  layer: LayerProps | undefined;
  legendFilter?: MapLayerFilter;
}) => {
  const routeParams = useParams();

  const isPlotPath = useIsPlotPage();

  if (!layer) {
    return null;
  }

  if (!routeParams.plotId) {
    return null;
  }
  // TODO: BIG HACK - MVP-7252 - remove once layer is applicable to all organizations
  if (layer.value === MAP_LAYER_FACTS.siemens_protected_areas) {
    return <SiemensProtectedAreasBorderLayer />;
  }

  if (isPlotPath) {
    return <GeojsonSourceLayerPlot selectedLayer={layer} legendFilter={legendFilter} />;
  }

  return <GeojsonSourceLayerOrg selectedLayer={layer} legendFilter={legendFilter} />;
};

const GeojsonSourceLayerPlot = ({
  selectedLayer,
  legendFilter,
}: {
  selectedLayer: LayerProps;
  legendFilter?: MapLayerFilter;
}) => {
  const geojsonFact = usePlotFacts([selectedLayer.value]);
  const geojsonData = getFactFromFactArray(geojsonFact, selectedLayer.value);

  const geojsonLayer = geojsonData?.value as FeatureCollection<Geometry, GeoJsonProperties> | undefined;
  const filteredGeojsonLayer = getFilteredGeojsonData({ geojsonLayer, legendFilter });

  return <GeojsonSourceLayerBase selectedLayer={selectedLayer} geojsonLayer={filteredGeojsonLayer} />;
};

const GeojsonSourceLayerOrg = ({
  selectedLayer,
  legendFilter,
}: {
  selectedLayer: LayerProps;
  legendFilter?: MapLayerFilter;
}) => {
  const geojsonData = useOrganizationIndicatorReportingFact(selectedLayer.value);

  const geojsonLayer = geojsonData?.[selectedLayer.value]?.value as
    | FeatureCollection<Geometry, GeoJsonProperties>
    | undefined;

  const filteredGeojsonLayer = getFilteredGeojsonData({ geojsonLayer, legendFilter });

  return <GeojsonSourceLayerBase selectedLayer={selectedLayer} geojsonLayer={filteredGeojsonLayer} />;
};

const getFilteredGeojsonData = ({
  geojsonLayer,
  legendFilter,
}: {
  geojsonLayer: FeatureCollection<Geometry, GeoJsonProperties> | undefined;
  legendFilter?: MapLayerFilter;
}) => {
  let filteredGeojsonLayer = geojsonLayer;
  const { key, value } = legendFilter || {};

  if (key && value && filteredGeojsonLayer) {
    filteredGeojsonLayer = {
      ...filteredGeojsonLayer,
      features: filteredGeojsonLayer?.features?.filter((feature) => {
        const featureValue = feature?.properties?.[`${key}`];

        if (Array.isArray(value)) {
          return value.includes(featureValue);
        }

        return featureValue === value;
      }),
    };
  }

  return filteredGeojsonLayer;
};

const GeojsonSourceLayerBase = ({
  selectedLayer,
  geojsonLayer,
}: {
  selectedLayer: LayerProps;
  geojsonLayer: FeatureCollection<Geometry, GeoJsonProperties> | undefined;
}) => {
  const controlsForm = useControlsContext();

  if (!geojsonLayer) {
    return null;
  }

  const [year] = controlsForm.watch(['year']);

  return (
    <Source
      key={`${selectedLayer}-${year}`} // Force re-render when layer or year changes
      id='heatmap-source-geojson'
      type='geojson'
      data={geojsonLayer}
    >
      {selectedLayer.raster && <RasterLayer {...(selectedLayer.raster as RasterProps)} />}
    </Source>
  );
};

const RasterLayer = (props: RasterProps) => {
  switch (props.type) {
    case 'circle':
      return <CircleLayer {...props} />;
    case 'line':
      return <LineLayer {...props} />;
    default:
      return null;
  }
};

const CircleLayer = ({ colors, dataKey }: RasterProps) => {
  if (isNullish(colors) || isNullish(dataKey)) {
    return null;
  }
  const colorExpression = [
    'case',
    ...Object.entries(colors).flatMap(([key, color]) => [['==', ['get', dataKey], key], color]),
    '#cccccc', // Fallback color
  ] as Expression;

  return (
    <Layer
      id={INTERACTIVE_HEATMAP_LAYER_ID}
      source='heatmap-source-geojson'
      type='circle'
      paint={{
        'circle-color': colorExpression,
        'circle-radius': 4,
        'circle-stroke-width': 1,
        'circle-stroke-color': '#fff',
      }}
    />
  );
};

const LineLayer = ({ color = '#cccccc', width = 2 }: RasterProps) => {
  return (
    <Layer
      id={INTERACTIVE_HEATMAP_LAYER_ID}
      source='heatmap-source-geojson'
      type='line'
      paint={{
        'line-color': color,
        'line-width': width,
      }}
    />
  );
};

// TODO: BIG HACK - MVP-7252 - remove once layer is applicable to all organizations
const SiemensProtectedAreasBorderLayer = () => {
  const data = useSiemensData();
  const controlsForm = useControlsContext();
  const layers = controlsForm.watch('layers');

  const safeData = data || { type: 'FeatureCollection', features: [] };

  return (
    <Source
      key={`${layers[0]}-${MAP_LAYER_FACTS.siemens_protected_areas}`}
      id='heatmap-source-geojson'
      type='geojson'
      data={safeData}
    >
      <Layer
        id={PROTECTED_AREAS_LAYER_ID}
        type='line'
        paint={{
          'line-color': SIEMENS_HACK_PROTECTED_AREAS_LAYER.raster.color,
          'line-width': SIEMENS_HACK_PROTECTED_AREAS_LAYER.raster.width,
        }}
      />
    </Source>
  );
};
